import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { ProductFormValues } from "../lib/schemas/travel-booking";
import { productSchema } from "../lib/schemas/travel-booking";
import {
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Edit, Trash } from "lucide-react";
import Select from "react-select";
import { getLiveRates } from "../apis";
import { FieldConfig } from "../lib/FormConfigs/config";
import { ConfigurableForm, renderField } from "../lib/FormConfigs/Renderer";
import { FormField } from "../ui-form/form-field";

interface ProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (product: ProductFormValues) => void;
  initialData?: ProductFormValues;
  order_type?: "buy" | "sell";
  config?: FieldConfig[];
}

// Mock data
const productTypes = [
  { value: "9", label: "Sale", type: "sell" },
  { value: "9", label: "Enchashment", type: "buy" },
  { value: "16", label: "Reload", type: "buy" },
  { value: "16", label: "Unload", type: "buy" },
  { value: "16", label: "New card issuance", type: "buy" },
];

const cardTypes = [
  { value: "multicurrency", label: "Multi-Currency" },
  { value: "singlecurrency", label: "Single Currency" },
];

const currenciesList = [
  { value: "usd", label: "USD" },
  { value: "eur", label: "EUR" },
  { value: "gbp", label: "GBP" },
  { value: "jpy", label: "JPY" },
  { value: "aud", label: "AUD" },
  { value: "cad", label: "CAD" },
];

const FOREX_CURRENCIES_NOTES_OPTIONS = [
  { label: "USD", value: "USD" },
  { label: "EUR", value: "EUR" },
  { label: "GBP", value: "GBP" },
  { label: "AUD", value: "AUD" },
  { label: "CAD", value: "CAD" },
  { label: "SGD", value: "SGD" },
  { label: "NZD", value: "NZD" },
  { label: "HKD", value: "HKD" },
  { label: "AED", value: "AED" },
  { label: "SAR", value: "SAR" },
  { label: "CHF", value: "CHF" },
  { label: "SEK", value: "SEK" },
  { label: "THB", value: "THB" },
  { label: "JPY", value: "JPY" },
  { label: "MYR", value: "MYR" },
  { label: "CNY", value: "CNY" },
  { label: "ZAR", value: "ZAR" },
  { label: "OMR", value: "OMR" },
  { label: "BHD", value: "BHD" },
  { label: "KWD", value: "KWD" },
  { label: "NOK", value: "NOK" },
  { label: "DKK", value: "DKK" },
  { label: "IDR", value: "IDR" },
  { label: "LKR", value: "LKR" },
  { label: "KRW", value: "KRW" },
  { label: "TRY", value: "TRY" },
  { label: "RUB", value: "RUB" },
  { label: "QAR", value: "QAR" },
  { label: "PHP", value: "PHP" },
  { label: "VND", value: "VND" },
  { label: "MUR", value: "MUR" },
  { label: "BDT", value: "BDT" },
];

const FOREX_CARD_CURRENCY_OPTIONS = [
  { label: "USD", value: "USD" },
  { label: "EUR", value: "EUR" },
  { label: "GBP", value: "GBP" },
  { label: "AUD", value: "AUD" },
  { label: "CAD", value: "CAD" },
  { label: "SGD", value: "SGD" },
  { label: "JPY", value: "JPY" },
  { label: "CHF", value: "CHF" },
  { label: "NZD", value: "NZD" },
  { label: "AED", value: "AED" },
  { label: "THB", value: "THB" },
  { label: "HKD", value: "HKD" },
  { label: "SAR", value: "SAR" },
  { label: "CNY", value: "CNY" },
];

const defaultProduct: ProductFormValues = {
  productType: { value: "", label: "" },
  currencies: [{ currency: { value: "", label: "" }, amount: 0 }],
  local_id: "0",
};

export function ProductModal({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  config,
}: ProductModalProps) {
  // State to track all products being added
  const [products, setProducts] = useState<ProductFormValues[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [currencyData, setCurrencyData] = useState<any>(null);
  // Create a form specifically for this modal
  const form = useForm<ProductFormValues>({
    resolver: zodResolver(productSchema),
    defaultValues: initialData || defaultProduct,
    mode: "onChange",
  });

  const configFile = config ? config : [];

  // Reset products when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setProducts([]);
      setEditingIndex(null);
    }
  }, [isOpen]);

  // Update form when initialData changes
  useEffect(() => {
    if (initialData) {
      form.reset(initialData);
    } else {
      form.reset(defaultProduct);
    }
  }, [initialData, form]);

  const selectedProductType = form.watch("productType");
  const currencies = form.watch("currencies") || [];

  const handleProductTypeChange = (option: any) => {
    form.setValue("productType", option, { shouldValidate: true });
    form.setValue("productType.type", option?.type, { shouldValidate: true });
    if (option?.value !== "16") {
      form.setValue("cardType", undefined, { shouldValidate: true });
    }
  };

  const handleCardTypeChange = (option: any) => {
    form.setValue("cardType", option, { shouldValidate: true });
  };

  const handleCurrencyChange = async (index: number, currencyOption: any) => {
    const updatedCurrencies = [...currencies];
    updatedCurrencies[index] = {
      ...updatedCurrencies[index],
      currency: currencyOption,
    };

    if (currencyOption?.value) {
      const rate = await getLiveRates({
        currency: currencyOption?.value.toUpperCase(),
        type: selectedProductType?.type === "sell" ? "Sell" : "Buy",
        fx_product:
          selectedProductType?.label === "New card issuance"
            ? "Card"
            : "Currency",
      });
      updatedCurrencies[index].rate = Number(rate?.rates) || 0;
    }

    form.setValue("currencies", updatedCurrencies, { shouldValidate: true });
  };

  useEffect(() => {
    const run = async () => {
      if (currencyData?.id !== undefined && currencyData?.val) {
        await handleCurrencyChange(currencyData?.id, currencyData?.val);
      }
    };

    run();
  }, [currencyData, selectedProductType?.label]);

  const handleAmountChange = (index: number, amount: string) => {
    const updatedCurrencies = [...currencies];
    updatedCurrencies[index] = {
      ...updatedCurrencies[index],
      amount: Number.parseFloat(amount) || 0,
    };
    form.setValue("currencies", updatedCurrencies, { shouldValidate: true });
  };

  const addCurrency = () => {
    form.setValue(
      "currencies",
      [
        ...currencies,
        { currency: { value: "", label: "" }, amount: 0, rate: 0 },
      ],
      {
        shouldValidate: true,
      }
    );
  };

  const removeCurrency = (index: number) => {
    const updatedCurrencies = currencies.filter((_, i) => i !== index);
    form.setValue("currencies", updatedCurrencies, { shouldValidate: true });
  };

  // Add current product to the list
  const addProductToList = async () => {
    const isValid = await form.trigger();
    if (isValid) {
      const productData = form.getValues();
      const random_id = crypto.randomUUID();

      if (editingIndex !== null) {
        // Update existing product
        const updatedProducts = [...products];
        updatedProducts[editingIndex] = productData;
        setProducts(updatedProducts);
        setEditingIndex(null);
      } else {
        // Add new product
        setProducts([...products, { ...productData, local_id: random_id }]);
      }

      // Reset form for next product
      form.reset(defaultProduct);
    }
  };

  // Edit a product from the list
  const editProduct = (index: number) => {
    setEditingIndex(index);
    form.reset(products[index]);
  };

  // Remove a product from the list
  const removeProduct = (index: number) => {
    setProducts(products.filter((_, i) => i !== index));
    if (editingIndex === index) {
      setEditingIndex(null);
      form.reset(defaultProduct);
    }
  };

  // Submit all products
  const handleSubmitAll = () => {
    // If there's a product in the form that hasn't been added to the list yet
    if (
      Object.keys(form.formState.dirtyFields).length > 0 &&
      form.formState.isValid
    ) {
      form.handleSubmit((data) => {
        const allProducts = [...products, data];
        submitAllProducts(allProducts);
      })();
    } else {
      // Just submit the products list if no new product is being edited
      submitAllProducts(products);
    }
  };

  const submitAllProducts = (productsToSubmit: ProductFormValues[]) => {
    // Here's the key fix - we need to handle all products at once
    productsToSubmit.forEach((product) => {
      onSubmit(product);
    });

    // Close modal and reset
    onClose();
    setProducts([]);
    form.reset(defaultProduct);
  };

  const isFormCompletelyFilled = () => {
    const values = form.getValues();
    for (const field of configFile) {
      if (field.required && !values[field.name]) {
        return false;
      }
    }
    if (!values.currencies || values.currencies.length === 0) return false;

    for (const currency of values.currencies) {
      if (!currency.currency || !currency.amount || currency.amount <= 0) {
        return false;
      }
    }

    return true;
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogPortal>
        <AlertDialogOverlay className=" bg-black-900 bg-opacity-60 animate-overlayShow fixed inset-0" />
        <AlertDialogContent className="max-w-[880px] p-0 rounded-lg bg-white-A700">
          <AlertDialogHeader className="flex flex-row justify-between py-6 px-10 border-b">
            {/* Left Side: Title + Subtext */}
            <div className="flex flex-col items-start">
              <h2 className="text-xl font-semibold text-gray-900">
                Add Product
              </h2>
              <p className="text-sm text-left text-gray-500 mt-1">
                Fill the required details
              </p>
            </div>
          </AlertDialogHeader>
          <div className="px-10 overflow-auto max-h-[700px]">
            <Accordion type="single" collapsible className="space-y-2">
              {products?.length > 0
                ? products?.map((product, index) => (
                    <AccordionItem
                      className="bg-[#F9F9F9] no-underline border-0 py-3 px-5 rounded-lg"
                      key={index}
                      value={`item-${index}`}
                    >
                      <AccordionTrigger className="flex hover:no-underline justify-between w-full items-center p-0">
                        <div className="flex gap-2 items-center">
                          <p>{product.productType.label}</p>
                          <p className="text-blue-700 flex items-center gap-2">
                            <small className="bg-blue-700 text-[10px] text-white-A700 rounded-3xl px-2 py-0.5">
                              INR
                            </small>
                            {product.currencies
                              .reduce(
                                (sum, c) =>
                                  sum + (c?.amount || 0) * (c?.rate || 1),
                                0
                              )
                              .toFixed(2)}
                          </p>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Currency</TableHead>
                              <TableHead>Amount</TableHead>
                              <TableHead>Rate</TableHead>
                              <TableHead>Total Value</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {product.currencies.map((currency, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{currency.currency.label}</TableCell>
                                <TableCell>{currency.amount}</TableCell>
                                <TableCell>{currency.rate}</TableCell>
                                <TableCell>
                                  {(currency.amount * currency?.rate!).toFixed(
                                    2
                                  )}
                                </TableCell>
                                <TableCell className="flex self-end space-x-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => removeProduct(index)}
                                  >
                                    <Trash className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </AccordionContent>
                    </AccordionItem>
                  ))
                : null}
            </Accordion>
            <div className="grid grid-cols-1 gap-6 max-w-sm mt-2">
              {/* Product Form */}
              <div className="rounded-md w-full">
                <FormProvider {...form}>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      addProductToList();
                    }}
                  >
                    <div className="space-y-4 w-full">
                      {configFile?.map((item, index) => {
                        if (
                          item.name === "cardType" &&
                          selectedProductType?.value !== "16"
                        ) {
                          return null;
                        }
                        if (item.name === "currencies") {
                          return currencies?.map((currency, idx) => (
                            <FormItem
                              key={idx}
                              className="flex flex-col w-full gap-2 items-end rounded-md"
                            >
                              <FormLabel className="flex items-center gap-2 justify-between w-full">
                                <span>
                                  Select the Currency.
                                  <span className="text-red-500">*</span>
                                </span>
                                {currency?.rate && (
                                  <p className="bg-[#FFFED9] py-1 px-2.5 rounded-md">
                                    Live rate:{" "}
                                    <span className="text-blue-700">
                                      {currency?.rate}
                                    </span>
                                  </p>
                                )}
                              </FormLabel>
                              <FormControl className="w-full">
                                <Select
                                  options={
                                    selectedProductType?.value === "16"
                                      ? FOREX_CURRENCIES_NOTES_OPTIONS
                                      : FOREX_CARD_CURRENCY_OPTIONS
                                  }
                                  menuPlacement="auto"
                                  menuPosition="absolute"
                                  menuShouldScrollIntoView={false}
                                  value={currency.currency}
                                  onChange={(option) =>
                                    setCurrencyData({ id: idx, val: option })
                                  }
                                  placeholder="Select currency"
                                  className="react-select-container w-full"
                                  classNamePrefix="react-select"
                                  components={{
                                    IndicatorSeparator: () => null,
                                  }}
                                  styles={{
                                    control: (base, state) => ({
                                      ...base,
                                      padding: "8px",
                                      borderRadius: "8px",
                                      borderColor: state.isFocused
                                        ? "#3b82f6"
                                        : base.borderColor,
                                      boxShadow: state.isFocused
                                        ? "0 0 0 1px #3b82f6"
                                        : base.boxShadow,
                                      "&:hover": {
                                        borderColor: "#3b82f6",
                                      },
                                    }),
                                    valueContainer: (base) => ({
                                      ...base,
                                      padding: "0 8px",
                                    }),
                                    singleValue: (base) => ({
                                      ...base,
                                      fontSize: "14px",
                                      fontWeight: 500,
                                      color: "#165FE3",
                                    }),
                                    menu: (base) => ({
                                      ...base,
                                      zIndex: 9999,
                                    }),
                                    menuList: (base) => ({
                                      ...base,
                                      maxHeight: "120px",
                                      overflowY: "auto",
                                    }),
                                    option: (base, state) => ({
                                      ...base,
                                      backgroundColor: state.isFocused
                                        ? "#f0f8ff"
                                        : "white",
                                      color: "#000",
                                      padding: "10px",
                                      cursor: "pointer",
                                      fontSize: "12px",
                                      fontWeight: 500,
                                    }),
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                              <div className="w-full">
                                <FormLabel className="text-sm text-[#33333]">
                                  Enter Amount.
                                  <span className="text-red-500">*</span>
                                </FormLabel>
                                <input
                                  type="number"
                                  value={currency.amount || ""}
                                  onChange={(e) =>
                                    handleAmountChange(idx, e.target.value)
                                  }
                                  placeholder="Select the Qty"
                                  className="w-full mt-2 p-4 text-[14px] text-[#666666] border border-gray-300 rounded-[8px] focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                                />
                                {form?.formState?.errors?.currencies?.[idx]
                                  ?.amount && (
                                  <p className="text-xs text-red-600 mt-1">
                                    {
                                      form?.formState?.errors?.currencies[idx]
                                        ?.amount?.message
                                    }
                                  </p>
                                )}
                              </div>
                              <div className="col-span-1 flex items-end">
                                {idx > 0 && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeCurrency(idx)}
                                  >
                                    ×
                                  </Button>
                                )}
                              </div>
                            </FormItem>
                          ));
                        }

                        return (
                          <FormField
                            key={index}
                            isOptionalField={item.required}
                            name={item.name}
                            label={item.label}
                            render={({ field }) => renderField(item, field)}
                          />
                        );
                      })}
                      {form.watch("cardType")?.value === "multicurrency" && (
                        <div className="inline-flex items-center border border-blue-600 text-blue-600 rounded-lg px-3 py-1 cursor-pointer hover:bg-blue-50 transition">
                          <span className="text-lg font-bold mr-1">+</span>
                          <Button
                            type="button"
                            variant="ghost"
                            className="text-blue-600 px-0 py-0 h-auto"
                            onClick={addCurrency}
                          >
                            Add Currency
                          </Button>
                        </div>
                      )}
                    </div>
                    <Button
                      type="submit"
                      disabled={!form.formState.isValid}
                      className="bg-blue-700 absolute bottom-4 right-4 justify-end text-white-A700 border border-blue-700"
                    >
                      {editingIndex !== null ? "Update Product" : "Add product"}
                    </Button>
                  </form>
                </FormProvider>
              </div>
            </div>
          </div>
          <AlertDialogFooter className="h-min flex items-center gap-2 flex-row py-5 px-10">
            <Button
              className="text-blue-700 border border-blue-700"
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              className="bg-blue-700 text-white-A700 border border-blue-700"
              type="button"
              onClick={handleSubmitAll}
              disabled={
                products.length === 0 &&
                (!form.formState.isValid || !isFormCompletelyFilled())
              }
            >
              Save
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogPortal>
    </AlertDialog>
  );
}
